import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import trackSelectedSalon from 'lib/utils/trackSelectedSalon';
import { useSettings } from 'contexts/SettingContext';
import {
  setStorageSelectedSalonNo,
  setStorageCustomerSalonName,
  setStorageCustomerSalonNo,
  getCountryByLocale,
} from 'lib/shopify';
import { get, keys } from 'lib/utils/localStorage';
import { handleGetDistance } from 'lib/utils/salonsHelpers';
import getSalons from 'lib/firebase/getSalons';
import { useCustomerSalon } from './useCustomerSalon';

// Salon search/sort/select logic shared by the global salonSelectorModal and
// the cart drawer SalonPanel. Extracted 1:1 from salonSelectorModal.jsx.
export function useSalonSearch({ locale, isOpen }) {
  const [selectedSalon, setSelectedSalon] = useState(null);
  const { salons, setSalons, currentSalon, setCurrentSalon, setSalonManuallyDeleted } =
    useSettings();
  const { setCustomerSalon } = useCustomerSalon(locale);
  const [filteredSalons, setFilteredSalons] = useState(salons);
  const [searchQuery, setSearchQuery] = useState('');
  const [myLocation, setMyLocation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [salonsSettled, setSalonsSettled] = useState(false);
  const [showingNearest, setShowingNearest] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (!isOpen || salons.length) return;
    getSalons(locale).then(res => {
      if (res?.length) setSalons(res);
      setSalonsSettled(true);
    });
  }, [isOpen, salons.length, locale]);

  const ambassadorPlusAffiliationSalons = useMemo(() => {
    const withDistance = salon => {
      if (!myLocation.latitude || !salon?.position?.latitude) return salon;
      return {
        ...salon,
        formattedDistance: (handleGetDistance(salon.position, myLocation) / 1000).toFixed(1),
      };
    };

    const compareSalons = (a, b) => {
      if (!myLocation.latitude) return a.salonName.trim().localeCompare(b.salonName.trim()); // if no location, sort by name
      return Number(a.formattedDistance ?? Infinity) - Number(b.formattedDistance ?? Infinity) || 0;
    };

    const country = getCountryByLocale(locale);
    const ambassadorSalons = [...salons]
      .filter(
        salon =>
          (salon.salonLoyalty || salon.client100) &&
          salon.salonNo !== currentSalon?.salonNo &&
          salon.country === country
      )
      .map(withDistance)
      .sort((a, b) => compareSalons(a, b));
    /** If the selectedSalon is a non-ambassador salon, put that as the first element, afterwards spread the rest */
    if (currentSalon) return [withDistance(currentSalon), ...ambassadorSalons];
    return [...ambassadorSalons];
  }, [salons, currentSalon, locale, myLocation]);

  useEffect(() => {
    const fetchLocationThroughIP = () => {
      fetch('/api/geolocation')
        .then(res => res.json())
        .then(location => {
          setLoading(false);
          setMyLocation({
            latitude: location.lat,
            longitude: location.lon,
          });
        })
        .catch(err => {
          console.error(err);
          setLoading(false);
        });
    };

    const getGeolocation = () => {
      // Try to get location by the Browser API
      navigator.geolocation.getCurrentPosition(
        position => {
          setLoading(false);
          setMyLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          // If the browser API geolocation fails, use the IP geolocation
          fetchLocationThroughIP();
        }
      );
    };

    if (isOpen && !myLocation) {
      setLoading(true);
      getGeolocation();
    }
  }, [isOpen]);

  useEffect(() => {
    const email = get(keys.LOGGED_IN_CUSTOMER);
    if (email) {
      setCustomerSalon(email, currentSalon?.salonName || '');
    }
    setSelectedSalon(currentSalon);
  }, [currentSalon]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const query = searchQuery.toLowerCase();
      const zipQuery = query.replace(/\s/g, '');
      const matches = query
        ? ambassadorPlusAffiliationSalons.filter(
            salon =>
              salon.salonName?.toLowerCase().includes(query) ||
              salon.AddressLine1?.toLowerCase().includes(query) ||
              salon.AddressLine2?.toLowerCase().includes(query) ||
              salon.city?.toLowerCase().includes(query) ||
              String(salon.ZipCode || salon.zipCode || '')
                .toLowerCase()
                .replace(/\s/g, '')
                .includes(zipQuery)
          )
        : ambassadorPlusAffiliationSalons;
      setFilteredSalons(matches.length ? matches : ambassadorPlusAffiliationSalons.slice(0, 5));
      setShowingNearest(Boolean(query) && !matches.length);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery, ambassadorPlusAffiliationSalons]);

  const confirmSelection = onConfirmed => {
    if (!selectedSalon) return;
    setSalonManuallyDeleted(false);
    setStorageSelectedSalonNo(locale, selectedSalon.salonNo);
    setStorageCustomerSalonName(locale, selectedSalon.salonName);
    setStorageCustomerSalonNo(locale, selectedSalon.salonNo);
    setCurrentSalon(selectedSalon);
    trackSelectedSalon({
      salonNo: selectedSalon.salonNo,
      eventName: 'select_salon',
      location: { action: router },
    });
    if (onConfirmed) {
      onConfirmed(selectedSalon);
    }
  };

  return {
    filteredSalons,
    searchQuery,
    setSearchQuery,
    selectedSalon,
    setSelectedSalon,
    loading: loading || (!salons.length && !salonsSettled),
    confirmSelection,
    showingNearest,
  };
}

export default useSalonSearch;
